/**
 * ReviewTab — evaluates the open Word document against a regulatory checklist.
 *
 * State machine: idle → loading_checklists → ready → extracting → evaluating → done | error
 */

import React, { useCallback, useEffect } from 'react';
import {
  Button,
  Alert,
  AlertDescription,
} from '@e14c/feathers';
import {
  Download,
  Renew,
  TaskApproved,
  CheckmarkFilled,
  ErrorFilled,
  SubtractAlt,
} from '@e14c/feathers/icons';

import { useReviewStore, type RuleReviewItem } from '../store';
import { RuleResultCard } from '../components/RuleResultCard';
import {
  listChecklists,
  getChecklist,
  submitEvaluation,
  pollEvaluation,
  type DocumentSection,
} from '../api/checklist';
import type { ReviewResultOut, ReviewSummary } from '../types';
import { useTabLayoutStyles } from '../styles/tabLayout';
import { TabScrollArea } from '../components/TabScrollArea';
import { downloadChecklistResultsCsv } from '../utils/csvExport';

// ---------------------------------------------------------------------------
// Office.js document extraction
// ---------------------------------------------------------------------------

interface DocumentContent {
  document: string;
  sections: DocumentSection[];
}

async function extractWordDocument(): Promise<DocumentContent> {
  if (typeof Word === 'undefined' || typeof Word.run !== 'function') {
    return { document: '[Document text not available outside Word]', sections: [] };
  }

  return Word.run(async (context) => {
    const body = context.document.body;
    const contentControls = context.document.contentControls;

    body.load('text');
    contentControls.load('title, text');
    await context.sync();

    const sections: DocumentSection[] = contentControls.items
      .filter((cc) => cc.title?.trim())
      .map((cc) => ({ title: cc.title.trim(), content: cc.text ?? '' }));

    return { document: body.text, sections };
  });
}

function findUnmatchedRuleIds(
  rules: { id: string; section_match: string | null; section_match_type: string | null }[],
  sections: DocumentSection[],
): Set<string> {
  const unmatched = new Set<string>();

  for (const rule of rules) {
    if (!rule.section_match || rule.section_match_type === 'document') continue;

    const matched =
      rule.section_match_type === 'regex'
        ? (() => {
            try {
              const re = new RegExp(rule.section_match, 'i');
              return sections.some((s) => re.test(s.title));
            } catch {
              return false;
            }
          })()
        : sections.some(
            (s) => s.title.toLowerCase() === rule.section_match!.toLowerCase(),
          );

    if (!matched) unmatched.add(rule.id);
  }

  return unmatched;
}

// ---------------------------------------------------------------------------
// Review summary bar
// ---------------------------------------------------------------------------

function shareOfTotal(count: number, total: number): number | null {
  if (total <= 0) return null;
  return Math.round((count / total) * 100);
}

const SummaryStat: React.FC<{
  icon: React.ReactNode;
  count: number;
  share: number | null;
  label: string;
  valueClass: string;
}> = ({ icon, count, share, label, valueClass }) => (
  <div className="flex flex-col items-center gap-0.5 min-w-0">
    <div className={`flex items-center gap-1 tabular-nums ${valueClass}`}>
      {icon}
      <span className="text-lg font-semibold">{count}</span>
    </div>
    <span className="text-xs text-muted-foreground">
      {label}
      {share !== null && ` (${share}%)`}
    </span>
  </div>
);

const ReviewSummaryBar: React.FC<{
  summary: ReviewSummary;
  totalRules: number;
  isEvaluating: boolean;
}> = ({ summary, totalRules, isEvaluating }) => {
  const done = summary.pass_count + summary.fail_count + summary.na_count;

  return (
    <div className="bg-muted rounded-md px-4 py-2 grid grid-cols-3 gap-2">
      <SummaryStat
        icon={<CheckmarkFilled size={18} className="text-highlight-success" />}
        count={summary.pass_count}
        share={shareOfTotal(summary.pass_count, summary.total)}
        label="Pass"
        valueClass="text-highlight-success"
      />
      <SummaryStat
        icon={<ErrorFilled size={18} className="text-highlight-error" />}
        count={summary.fail_count}
        share={shareOfTotal(summary.fail_count, summary.total)}
        label="Fail"
        valueClass="text-highlight-error"
      />
      <SummaryStat
        icon={<SubtractAlt size={18} className="text-muted-foreground" />}
        count={summary.na_count}
        share={shareOfTotal(summary.na_count, summary.total)}
        label="N/A"
        valueClass="text-muted-foreground"
      />
      {isEvaluating && totalRules > 0 && (
        <div className="col-span-3 flex items-center justify-center gap-2 mt-0.5">
          <span className="inline-block size-3 animate-spin rounded-full border-2 border-muted-foreground border-t-transparent" />
          <span className="text-xs text-muted-foreground">
            {done}/{totalRules} rules evaluated
          </span>
        </div>
      )}
    </div>
  );
};

const sortResultsByTitle = (results: ReviewResultOut[]): ReviewResultOut[] =>
  [...results].sort((a, b) =>
    a.rule_title.localeCompare(b.rule_title, undefined, { numeric: true }),
  );

const sortRuleItemsByTitle = (items: RuleReviewItem[]): RuleReviewItem[] =>
  [...items].sort((a, b) =>
    a.rule_title.localeCompare(b.rule_title, undefined, { numeric: true }),
  );

// ---------------------------------------------------------------------------
// Rule list / results section
// ---------------------------------------------------------------------------

const RuleListSection: React.FC<{
  ruleItems: RuleReviewItem[];
  summary: ReviewSummary;
  isEvaluating: boolean;
  checklistName?: string | null;
  onReset?: () => void;
}> = ({ ruleItems, summary, isEvaluating, checklistName, onReset }) => {
  const doneResults = sortResultsByTitle(
    ruleItems
      .map((item) => item.result)
      .filter((result): result is ReviewResultOut => result !== null),
  );

  const handleDownloadCsv = useCallback(() => {
    downloadChecklistResultsCsv(doneResults, checklistName);
  }, [checklistName, doneResults]);

  return (
    <TabScrollArea>
      <ReviewSummaryBar
        summary={summary}
        totalRules={ruleItems.length}
        isEvaluating={isEvaluating}
      />

      <div className="flex items-center justify-between gap-2">
        {isEvaluating ? (
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <span className="inline-block size-4 animate-spin rounded-full border-2 border-muted-foreground border-t-transparent" />
            Evaluating checklist rules…
          </div>
        ) : (
          <span className="text-xs text-muted-foreground">
            {ruleItems.length} rule{ruleItems.length !== 1 ? 's' : ''} evaluated
          </span>
        )}
        <div className="flex items-center gap-1 shrink-0">
          {doneResults.length > 0 && (
            <Button
              variant="ghost"
              size="sm"
              onClick={handleDownloadCsv}
              aria-label="Download checklist results as CSV"
            >
              <Download size={16} />
              CSV
            </Button>
          )}
          {onReset && (
            <Button variant="ghost" size="sm" onClick={onReset}>
              <Renew size={16} />
              New scan
            </Button>
          )}
        </div>
      </div>

      {sortRuleItemsByTitle(ruleItems).map((item) => (
        <RuleResultCard
          key={item.rule_id}
          ruleTitle={item.rule_title}
          result={item.result}
          pending={item.evaluationStatus === 'pending'}
        />
      ))}
    </TabScrollArea>
  );
};

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

export const ReviewTab: React.FC = () => {
  const layout = useTabLayoutStyles();

  const phase = useReviewStore((s) => s.phase);
  const checklists = useReviewStore((s) => s.checklists);
  const selectedChecklistId = useReviewStore((s) => s.selectedChecklistId);
  const ruleItems = useReviewStore((s) => s.ruleItems);
  const globalError = useReviewStore((s) => s.globalError);
  const setPhase = useReviewStore((s) => s.setPhase);
  const setChecklists = useReviewStore((s) => s.setChecklists);
  const setSelectedChecklistId = useReviewStore(
    (s) => s.setSelectedChecklistId,
  );
  const setEvaluationJobId = useReviewStore((s) => s.setEvaluationJobId);
  const initRuleItems = useReviewStore((s) => s.initRuleItems);
  const mergeEvaluationResults = useReviewStore((s) => s.mergeEvaluationResults);
  const finalizeRuleItems = useReviewStore((s) => s.finalizeRuleItems);
  const setGlobalError = useReviewStore((s) => s.setGlobalError);
  const reset = useReviewStore((s) => s.reset);
  const summary = useReviewStore((s) => s.summary);

  useEffect(() => {
    if (phase !== 'idle') return;
    setPhase('loading_checklists');

    listChecklists()
      .then((resp) => {
        if (resp.data) {
          setChecklists(resp.data.checklists);
          setPhase('ready');
        } else {
          setGlobalError(resp.error?.message ?? 'Failed to load checklists');
          setPhase('error');
        }
      })
      .catch((err: unknown) => {
        const msg = err instanceof Error ? err.message : String(err);
        setGlobalError(msg);
        setPhase('error');
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phase]);

  const handleScanDocument = useCallback(async () => {
    if (!selectedChecklistId) return;
    try {
      setPhase('extracting');
      setGlobalError(null);

      const [content, checklistResp] = await Promise.all([
        extractWordDocument(),
        getChecklist(selectedChecklistId),
      ]);

      const rules = checklistResp.data?.rules ?? [];
      const unmatchedRuleIds = findUnmatchedRuleIds(rules, content.sections);

      initRuleItems(rules, unmatchedRuleIds);

      // All rules already resolved as N/A (e.g. no matching content controls) —
      // skip the evaluation job so we don't spin forever waiting on an empty/no-op poll.
      const rulesNeedingEvaluation = rules.filter(
        (rule) => !unmatchedRuleIds.has(rule.id),
      );
      if (rulesNeedingEvaluation.length === 0) {
        setPhase('done');
        return;
      }

      setPhase('evaluating');

      const resp = await submitEvaluation({
        checklist_id: selectedChecklistId,
        content,
      });

      if (!resp.data) {
        throw new Error(resp.error?.message ?? 'Failed to start evaluation');
      }

      setEvaluationJobId(resp.data.job_id);
      const job = await pollEvaluation(resp.data.job_id, (data) => {
        if (data.results && data.results.length > 0) {
          mergeEvaluationResults(data.results);
        }
      });

      if (!job.results || !job.summary) {
        throw new Error('Evaluation completed but returned no results');
      }

      finalizeRuleItems(job.results, unmatchedRuleIds, rules);
      setPhase('done');
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : String(err);
      setGlobalError(msg);
      setPhase('error');
    }
  }, [
    selectedChecklistId,
    setPhase,
    setGlobalError,
    setEvaluationJobId,
    initRuleItems,
    mergeEvaluationResults,
    finalizeRuleItems,
  ]);

  const summaryData = summary();
  const selectedChecklist =
    checklists.find((cl) => cl.id === selectedChecklistId) ?? null;

  if (phase === 'loading_checklists' || phase === 'extracting') {
    const label =
      phase === 'loading_checklists'
        ? 'Loading checklists…'
        : 'Reading document…';

    return (
      <div className={layout.panel}>
        <div className={layout.centered}>
          <span className="inline-block size-8 animate-spin rounded-full border-[3px] border-muted-foreground border-t-transparent" />
          <p className="text-sm font-semibold text-foreground/70">{label}</p>
        </div>
      </div>
    );
  }

  if ((phase === 'evaluating' || phase === 'done') && ruleItems.length > 0) {
    return (
      <div className={layout.panel}>
        <RuleListSection
          ruleItems={ruleItems}
          summary={summaryData}
          isEvaluating={phase === 'evaluating'}
          checklistName={selectedChecklist?.name}
          onReset={phase === 'done' ? reset : undefined}
        />
      </div>
    );
  }

  if (phase === 'error' && checklists.length === 0) {
    return (
      <div className={layout.panel}>
        <div className={layout.centered}>
          <TaskApproved size={40} className="text-muted-foreground" />
          <p className="text-sm font-semibold text-foreground/70">
            Could not load checklists
          </p>
          {globalError && (
            <p className="text-xs text-muted-foreground">{globalError}</p>
          )}
          <Button size="sm" onClick={reset}>
            <Renew size={16} />
            Retry
          </Button>
        </div>
      </div>
    );
  }

  if (checklists.length === 0) {
    return (
      <div className={layout.panel}>
        <div className={layout.centered}>
          <TaskApproved size={40} className="text-muted-foreground" />
          <p className="text-base font-semibold text-foreground/70">
            No checklists yet
          </p>
          <p className="text-sm text-muted-foreground">
            Create a checklist with rules in the Manage tab, then come back
            here to scan your document.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className={layout.panel}>
      <TabScrollArea>
        {phase === 'error' && globalError && (
          <Alert variant="destructive">
            <AlertDescription className="flex items-center justify-between gap-2">
              <span>{globalError}</span>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  setGlobalError(null);
                  setPhase('ready');
                }}
              >
                Dismiss
              </Button>
            </AlertDescription>
          </Alert>
        )}

        <div className="space-y-1">
          <label className="text-xs text-muted-foreground block">Checklist</label>
          <select
            className="w-full h-9 rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-xs focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-ring/50 focus-visible:border-ring disabled:cursor-not-allowed disabled:opacity-50"
            value={selectedChecklistId ?? ''}
            onChange={(e) => setSelectedChecklistId(e.target.value || null as unknown as string)}
          >
            <option value="">— Select a checklist —</option>
            {checklists.map((cl) => (
              <option key={cl.id} value={cl.id}>
                {cl.name}
              </option>
            ))}
          </select>
        </div>

        <Button
          className="w-full"
          disabled={!selectedChecklistId}
          onClick={handleScanDocument}
        >
          Scan Document
        </Button>

        {!selectedChecklist && (
          <div className={layout.centered}>
            <TaskApproved size={36} className="text-muted-foreground" />
            <p className="text-sm text-muted-foreground">
              Select a checklist above, then click Scan Document to evaluate
              your document against all rules.
            </p>
          </div>
        )}
      </TabScrollArea>
    </div>
  );
};
